import * as React from 'react';
import { Navigate } from 'react-router-dom';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { alpha } from '@mui/material/styles';

import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import WbSunnyOutlinedIcon from '@mui/icons-material/WbSunnyOutlined';
import AccountCircleRoundedIcon from '@mui/icons-material/AccountCircleRounded';

import SolarIcon from '../icons/SolarIcon';

import AuthService from '../services/auth';
import OrgLogo from '../components/OrgLogo';
import eventReportService from '../services/event-report';
import AppConfig from '../services/app-config';
import apiClient, { shouldRedirectToLogin } from '../services/api-client';
import BannerNotificationsStack from '../components/BannerNotificationsStack';
import WeatherModal from '../components/WeatherModal';
import LocationModal from '../components/LocationModal';
import GestionTypeIcon from '../components/GestionTypeIcon';

const ResidentHub = () => {
    const [org, setOrg] = React.useState("");
    const [weather, setWeather] = React.useState({ temp: "", icon: 'NONE',fetched: false });
    const [redirect, setRedirect] = React.useState({ trigger: false, route: '/' });
    const [status, setStatus] = React.useState({ issues: null, amenities: null, pending_requests: null, recent_activity: [] });
    const [bannerNotifications, setBannerNotifications] = React.useState([]);
    const [editingQuickActions, setEditingQuickActions] = React.useState(false);
    const [quickActionOrder, setQuickActionOrder] = React.useState(null);
    const [addPickerOpen, setAddPickerOpen] = React.useState(false);
    const [weatherModalOpen, setWeatherModalOpen] = React.useState(false);
    const [locationModalOpen, setLocationModalOpen] = React.useState(false);
    const [gestionTypes, setGestionTypes] = React.useState([]);
    const dragActionIdRef = React.useRef(null);

    let event = new URLSearchParams(location.search).get('event');

    const getWeather = async () => {
        try {
            const response = await apiClient.get('weather/current', {}, { action: 'fetch_weather' });
            setWeather({ temp: response.data.data.temp, icon: response.data.data.icon, fetched: true });
        } catch (error) {
            console.error(error);
        }
    }

    const getWeatherShortLabel = React.useCallback((icon) => {
        switch (String(icon || '').toUpperCase()) {
            case 'CLEAR_DAY':
                return 'Soleado';
            case 'PARTLY_CLOUDY_DAY':
                return 'Parcial';
            case 'CLOUDY':
                return 'Nublado';
            case 'RAIN':
                return 'Lluvia';
            case 'SLEET':
                return 'Aguanieve';
            case 'SNOW':
                return 'Nieve';
            case 'FOG':
                return 'Niebla';
            case 'CLEAR_NIGHT':
                return 'Despejado';
            case 'PARTLY_CLOUDY_NIGHT':
                return 'Parcial';
            default:
                return 'Clima';
        }
    }, []);

    const nowLabel = React.useMemo(() => {
        try {
            return new Date().toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' });
        } catch (e) {
            return '';
        }
    }, []);

    const fetchStatus = async () => {
        try {
            const response = await apiClient.get('habitante/status', {}, { action: 'fetch_habitante_status' });
            AppConfig.parseResponse(response.data);
            setStatus(response.data.data);
            setBannerNotifications(response.data?.data?.banner_notifications || []);

        } catch (error) {
            console.error(error);
            if (shouldRedirectToLogin(error)) {
                setRedirect({ trigger: true, route: '/login' })
              }
        }
    }

    const fetchGestionTypes = React.useCallback(async () => {
        try {
            const response = await apiClient.get('gestiones/types', { parseAppConfig: false }, { action: 'fetch_gestion_types' });
            if (response.status === 200 && Array.isArray(response.data?.data)) {
                setGestionTypes(response.data.data);
            } else if (response.status === 200 && Array.isArray(response.data?.data?.types)) {
                setGestionTypes(response.data.data.types);
            }
        } catch (err) {
            // Non-fatal: gestion types are optional.
            console.debug('Failed to load gestion types', err);
        }
    }, []);

    React.useEffect(() => {
        setOrg(AuthService.getCurrentUserOrg());
        fetchStatus();
        getWeather();
        fetchGestionTypes();
        try {
            const raw = localStorage.getItem('residenthub_quick_actions_order_v1');
            if (raw !== null) {
                const stored = JSON.parse(raw);
                if (Array.isArray(stored)) {
                    setQuickActionOrder(stored);
                }
            }
        } catch (e) {
            // ignore
        }
        if (event !== null) {
            console.log(event)
            eventReportService(event, { device: AppConfig.getDevicePlatform(), installed: AppConfig.isInstalled() });
            setRedirect({ trigger: true, route: '/residenthub' });
            event = null;
        }
    }, []);

    const persistQuickActionOrder = React.useCallback((order) => {
        try {
            localStorage.setItem('residenthub_quick_actions_order_v1', JSON.stringify(order));
        } catch (e) {
            // ignore
        }
    }, []);

    const DEFAULT_QUICK_ACTION_ORDER = React.useMemo(() => (['visitas', 'amenidades', 'airbnb', 'problemas', 'directorio', 'paqueteria']), []);

    const buildQuickActionDefs = React.useCallback(() => {
        const items = [];

        const showVisits = AppConfig.haveModule('visits');
        const showAmenities = AppConfig.haveModule('amenities');
        const showAirbnb = AppConfig.haveModule('airbnb') && AppConfig.get('unit_mode') === 'airbnb';
        const showIssues = AppConfig.haveModule('issues');
        const showDirectory = AppConfig.get('appear_in_directory') && AppConfig.haveModule('directory');
        const showPaqueteria = AppConfig.haveModule('paqueteria');

        if (showVisits) {
            items.push({ id: 'visitas', label: 'Visitas', icon: <SolarIcon name="users-group-rounded" size={26} color="#238048" />, route: 'visitas' });
        }
        if (showAmenities) {
            items.push({ id: 'amenidades', label: 'Amenidades', icon: <SolarIcon name="swimming" size={26} color="#238048" />, route: 'amenidades' });
        }
        if (showAirbnb) {
            items.push({ id: 'airbnb', label: 'Airbnb', icon: <SolarIcon name="bed" size={26} color="#238048" />, route: 'airbnb' });
        }
        if (showIssues) {
            items.push({ id: 'problemas', label: 'Averías', icon: <SolarIcon name="shield-warning" size={26} color="#238048" />, route: 'reportes' });
        }
        if (showDirectory) {
            items.push({ id: 'directorio', label: 'Directorio', icon: <SolarIcon name="notebook" size={26} color="#238048" />, route: 'directorio' });
        }
        if (showPaqueteria) {
            items.push({ id: 'paqueteria', label: 'Paquetes', icon: <SolarIcon name="box" size={26} color="#238048" />, route: 'paquetes' });
        }

        for (const gtype of gestionTypes) {
            const moduleKey = `gestiones:${gtype.key}`;
            if (!AppConfig.haveModule(moduleKey)) {
                continue;
            }
            items.push({
                id: `gestiones_${gtype.key}`,
                label: gtype.nombre || gtype.key,
                icon: <GestionTypeIcon typeKey={gtype.key} size={26} color="#238048" />,
                route: `gestiones/${gtype.key}`,
            });
        }

        return items;
    }, [gestionTypes]);

    const quickActions = React.useMemo(() => {
        const defs = buildQuickActionDefs();
        const byId = new Map(defs.map((d) => [d.id, d]));

        const baseOrder = quickActionOrder === null ? DEFAULT_QUICK_ACTION_ORDER : quickActionOrder;
        const ordered = [];

        for (const id of baseOrder) {
            const item = byId.get(id);
            if (item) {
                ordered.push(item);
                byId.delete(id);
            }
        }

        return ordered;
    }, [buildQuickActionDefs, DEFAULT_QUICK_ACTION_ORDER, quickActionOrder]);

    const availableToAdd = React.useMemo(() => {
        const defs = buildQuickActionDefs();
        const visible = new Set(quickActions.map((d) => d.id));
        return defs.filter((d) => !visible.has(d.id));
    }, [buildQuickActionDefs, quickActions]);

    const handleRemoveQuickAction = React.useCallback((id) => {
        setQuickActionOrder((prev) => {
            const current = prev === null ? DEFAULT_QUICK_ACTION_ORDER : prev;
            const next = current.filter((x) => x !== id);
            persistQuickActionOrder(next);
            return next;
        });
    }, [DEFAULT_QUICK_ACTION_ORDER, persistQuickActionOrder]);

    const handleAddQuickAction = React.useCallback((id) => {
        setQuickActionOrder((prev) => {
            const current = prev === null ? DEFAULT_QUICK_ACTION_ORDER : prev;
            if (current.includes(id)) {
                return current;
            }
            const next = [...current, id];
            persistQuickActionOrder(next);
            return next;
        });
    }, [DEFAULT_QUICK_ACTION_ORDER, persistQuickActionOrder]);

    const handleClick = (route) => {
        setRedirect({ trigger: true, route: `/${route.toLowerCase()}` })
    }

    const navigateToPath = React.useCallback((path) => {
        if (!path) {
            return;
        }
        const trimmed = String(path).trim();
        if (!trimmed) {
            return;
        }
        const normalized = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
        setRedirect({ trigger: true, route: normalized });
    }, [setRedirect]);

    const markBannerAction = React.useCallback(async (notificationId, action) => {
        if (!notificationId) {
            return;
        }
        try {
            await apiClient.post('banner_notifications/action', {
                notification_id: notificationId,
                action,
            }, {}, { action: `resident_banner_${action}` });
        } catch (error) {
            console.error(error);
        }
    }, []);

    const handleDismissBanner = React.useCallback((notificationId) => {
        if (!notificationId) {
            return;
        }
        setBannerNotifications((prev) => prev.filter((banner) => banner.id !== notificationId));
        markBannerAction(notificationId, 'dismiss');
    }, [markBannerAction]);

    const handleViewBanner = React.useCallback((notificationId) => {
        markBannerAction(notificationId, 'view');
    }, [markBannerAction]);

    const navigateToBannerUrl = React.useCallback((url) => {
        if (!url) {
            return;
        }

        const trimmed = url.trim();

        if (!trimmed) {
            return;
        }

        const isAbsolute = /^https?:\/\//i.test(trimmed);

        if (isAbsolute) {
            window.open(trimmed, '_blank', 'noopener,noreferrer');
            return;
        }

        const normalized = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
        setRedirect({ trigger: true, route: normalized });
    }, [setRedirect]);

    const activityIcon = React.useCallback((name) => {
        switch (name) {
            case 'plumbing':
                return <SolarIcon name="shield-warning" size={24} color="#238048" />;
            case 'local_shipping':
                return <SolarIcon name="box" size={24} color="#238048" />;
            case 'celebration':
                return <SolarIcon name="gift" size={24} color="#238048" />;
            case 'event_busy':
                return <SolarIcon name="calendar" size={24} color="#238048" />;
            case 'person_add':
                return <SolarIcon name="user-plus" size={24} color="#238048" />;
            case 'directions_car':
                return <SolarIcon name="garage" size={24} color="#238048" />;
            case 'meeting_room':
                return <SolarIcon name="users-group-rounded" size={24} color="#238048" />;
            case 'local_cafe':
            default:
                return <SolarIcon name="cup-hot" size={24} color="#238048" />;
        }
    }, []);

    const badgeSx = (colorName) => {
        const crimson = '#c92c51';
        const forest = '#238048';
        const color = colorName === 'forest' ? forest : crimson;
        const bg = colorName === 'forest' ? 'rgba(125, 217, 158, 0.50)' : `${color}1A`;

        return {
            display: 'inline-flex',
            alignItems: 'center',
            px: 1.25,
            py: 0.75,
            borderRadius: '10px',
            fontSize: '10px',
            fontWeight: 800,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            backgroundColor: bg,
            color,
        };
    };

    const onQuickActionDragStart = React.useCallback((actionId) => {
        dragActionIdRef.current = actionId;
    }, []);

    const onQuickActionDrop = React.useCallback((overActionId) => {
        const draggedId = dragActionIdRef.current;
        dragActionIdRef.current = null;

        if (!draggedId || !overActionId || draggedId === overActionId) {
            return;
        }

        setQuickActionOrder((prev) => {
            const base = prev === null ? [...DEFAULT_QUICK_ACTION_ORDER] : [...prev];
            const fromIdx = base.indexOf(draggedId);
            const toIdx = base.indexOf(overActionId);
            if (fromIdx === -1 || toIdx === -1) {
                return base;
            }
            base.splice(fromIdx, 1);
            base.splice(toIdx, 0, draggedId);
            persistQuickActionOrder(base);
            return base;
        });
    }, [DEFAULT_QUICK_ACTION_ORDER, persistQuickActionOrder]);

    return (
        <Container maxWidth="sm" disableGutters>
            {redirect.trigger && (
                <Navigate to={redirect.route} replace={true} />
            )}
            <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', pb: 6 }}>
                <Box
                    sx={{
                        position: 'sticky',
                        top: 0,
                        zIndex: 10,
                        px: 3,
                        pt: 3,
                        pb: 2,
                        backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.95),
                        backdropFilter: 'blur(10px)',
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75 }}>
                            {org ? (
                                <OrgLogo
                                    org={String(org).toLowerCase()}
                                    alt={`${org}-logo`}
                                    sx={{
                                        width: 48,
                                        height: 48,
                                        borderRadius: '10px',
                                        objectFit: 'contain',
                                        backgroundColor: 'background.paper',
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        p: 0.5,
                                    }}
                                />
                            ) : null}
                            <Box>
                                <Typography sx={{ fontSize: '0.758rem', fontWeight: 800, opacity: 0.65, letterSpacing: 1 }}>
                                    Hola, {AuthService.getCurrentUser() || ''}
                                </Typography>
                                <Typography sx={{ fontSize: '1.375rem', fontWeight: 900, lineHeight: 1.1, letterSpacing: -0.5, color: 'text.primary' }}>
                                    {AppConfig.get('friendly_name') || org || ''}
                                </Typography>
                            </Box>
                        </Box>
                        <IconButton
                            sx={{
                                width: 40,
                                height: 40,
                                borderRadius: '10px',
                                color: '#238048',
                            }}
                            onClick={() => handleClick('perfil')}
                        >
                            <AccountCircleRoundedIcon />
                        </IconButton>
                    </Box>

                    <Stack direction="row" spacing={1.25} sx={{ overflowX: 'auto', pb: 0.5, '&::-webkit-scrollbar': { display: 'none' }, scrollbarWidth: 'none' }}>
                        <Chip
                            variant="outlined"
                            icon={weather.fetched ? <WbSunnyOutlinedIcon /> : <CircularProgress size={16} />}
                            label={weather.fetched ? `${getWeatherShortLabel(weather.icon)} ${weather.temp}°C` : 'Cargando clima'}
                            onClick={() => setWeatherModalOpen(true)}
                            sx={{
                                borderRadius: '10px',
                                borderColor: 'divider',
                                color: 'text.primary',
                                height: 36,
                                flexShrink: 0,
                                backgroundColor: 'background.paper',
                                cursor: 'pointer',
                                '& .MuiChip-icon': { color: 'text.primary' },
                                '& .MuiChip-label': { fontWeight: 700, fontSize: '0.75rem' },
                            }}
                        />
                        <Chip
                            variant="outlined"
                            icon={<SolarIcon name="map-point" size={24} />}
                            label={AppConfig.get('friendly_name') || org || '—'}
                            onClick={() => setLocationModalOpen(true)}
                            sx={{
                                borderRadius: '10px',
                                borderColor: 'divider',
                                color: 'text.primary',
                                height: 36,
                                flexShrink: 0,
                                backgroundColor: 'background.paper',
                                cursor: 'pointer',
                                '& .MuiChip-icon': { color: 'text.primary' },
                                '& .MuiChip-label': { fontWeight: 700, fontSize: '0.75rem' },
                            }}
                        />
                        <Chip
                            variant="outlined"
                            icon={<SolarIcon name="calendar" size={24} />}
                            label={nowLabel || '—'}
                            sx={{
                                borderRadius: '10px',
                                borderColor: 'divider',
                                color: 'text.primary',
                                height: 36,
                                flexShrink: 0,
                                backgroundColor: 'background.paper',
                                '& .MuiChip-icon': { color: 'text.primary' },
                                '& .MuiChip-label': { fontWeight: 700, fontSize: '0.75rem' },
                            }}
                        />
                    </Stack>
                </Box>

                <Box sx={{ px: 3, mt: 1.5, display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <BannerNotificationsStack
                        banners={bannerNotifications}
                        onNavigate={navigateToBannerUrl}
                        onView={handleViewBanner}
                        onDismiss={handleDismissBanner}
                    />

                    <Box id="quick-actions">
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                            <Typography sx={{ fontSize: '1.125rem', fontWeight: 800, color: 'text.primary' }}>
                                Acciones Rápidas
                            </Typography>
                            {editingQuickActions ? (
                                <Button
                                    onClick={() => setEditingQuickActions(false)}
                                    sx={{
                                        color: '#238048',
                                        fontWeight: 800,
                                        textTransform: 'none',
                                        p: 0,
                                        minWidth: 0,
                                        fontSize: '0.875rem',
                                    }}
                                >
                                    guardar
                                </Button>
                            ) : (
                                <Button
                                    onClick={() => setEditingQuickActions(true)}
                                    sx={{
                                        color: '#238048',
                                        fontWeight: 800,
                                        textTransform: 'none',
                                        p: 0,
                                        minWidth: 0,
                                        fontSize: '0.875rem',
                                    }}
                                >
                                    editar
                                </Button>
                            )}
                        </Box>

                        {!editingQuickActions && (
                            <Box sx={{ overflowX: 'auto', pb: 0.5 }}>
                                <Box sx={{ display: 'flex', gap: 2, width: 'max-content' }}>
                                    {quickActions.map((action) => (
                                        <Box
                                            key={action.id}
                                            sx={{ textAlign: 'center', cursor: 'pointer', width: 72, pt: 1, minHeight: 96 }}
                                            onClick={() => handleClick(action.route)}
                                        >
                                            <Box sx={{ position: 'relative', width: 56, height: 56, mx: 'auto' }}>
                                                <Box sx={{ width: 56, height: 56, borderRadius: '999px', backgroundColor: 'rgba(125, 217, 158, 0.20)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                    {action.icon}
                                                </Box>
                                                {action.id === 'paqueteria' && status.pending_packages > 0 && (
                                                    <Box sx={{ position: 'absolute', top: 2, right: -2, minWidth: 20, height: 20, px: 0.5, borderRadius: '999px', backgroundColor: '#c92c51', color: '#fff', fontSize: '0.6875rem', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid', borderColor: 'background.paper' }}>
                                                        {status.pending_packages > 9 ? '9+' : status.pending_packages}
                                                    </Box>
                                                )}
                                            </Box>
                                            <Typography sx={{ mt: 1, fontSize: '0.75rem', fontWeight: 600, color: 'text.primary' }}>{action.label}</Typography>
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        )}

                        {editingQuickActions && (
                            quickActions.length === 0 ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                                    <Box
                                        onClick={() => availableToAdd.length > 0 && setAddPickerOpen(true)}
                                        sx={{ textAlign: 'center', cursor: 'pointer', width: 72 }}
                                    >
                                        <Box sx={{ width: 56, height: 56, mx: 'auto', borderRadius: '999px', border: '2px dashed', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.secondary' }}>
                                            <AddIcon sx={{ fontSize: '1.625rem' }} />
                                        </Box>
                                        <Typography sx={{ mt: 1, fontSize: '0.75rem', fontWeight: 600, color: 'text.secondary' }}>Agregar</Typography>
                                    </Box>
                                </Box>
                            ) : (
                                <Grid container spacing={2}>
                                    {quickActions.map((action) => (
                                        <Grid key={action.id} item xs={3}>
                                            <Box
                                                draggable
                                                onDragStart={() => onQuickActionDragStart(action.id)}
                                                onDragOver={(evt) => evt.preventDefault()}
                                                onDrop={() => onQuickActionDrop(action.id)}
                                                sx={{
                                                    textAlign: 'center',
                                                    cursor: 'grab',
                                                    pt: 1,
                                                    minHeight: 96,
                                                    '@keyframes quickActionWiggle': {
                                                        '0%': { transform: 'rotate(-3deg) translateY(0px)' },
                                                        '25%': { transform: 'rotate(3deg) translateY(-4px)' },
                                                        '50%': { transform: 'rotate(-3deg) translateY(0px)' },
                                                        '75%': { transform: 'rotate(3deg) translateY(-4px)' },
                                                        '100%': { transform: 'rotate(-3deg) translateY(0px)' },
                                                    },
                                                }}
                                            >
                                                <Box sx={{ position: 'relative', width: 56, height: 56, mx: 'auto' }}>
                                                    <Box
                                                        sx={{
                                                            width: 56,
                                                            height: 56,
                                                            borderRadius: '999px',
                                                            backgroundColor: 'rgba(125, 217, 158, 0.20)',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            animation: 'quickActionWiggle 0.6s infinite ease-in-out',
                                                        }}
                                                    >
                                                        {action.icon}
                                                    </Box>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleRemoveQuickAction(action.id)}
                                                        sx={{
                                                            position: 'absolute',
                                                            top: 0,
                                                            right: -6,
                                                            width: 20,
                                                            height: 20,
                                                            p: 0,
                                                            backgroundColor: 'error.main',
                                                            color: '#fff',
                                                            boxShadow: 1,
                                                            '&:hover': { backgroundColor: 'error.main', opacity: 0.85 },
                                                        }}
                                                    >
                                                        <CloseIcon sx={{ fontSize: '0.875rem' }} />
                                                    </IconButton>
                                                </Box>
                                                <Typography sx={{ mt: 1, fontSize: '0.75rem', fontWeight: 600, color: 'text.primary' }}>{action.label}</Typography>
                                            </Box>
                                        </Grid>
                                    ))}
                                    {availableToAdd.length > 0 && (
                                        <Grid item xs={3}>
                                            <Box
                                                onClick={() => setAddPickerOpen(true)}
                                                sx={{ textAlign: 'center', cursor: 'pointer', width: 72, mx: 'auto' }}
                                            >
                                                <Box sx={{ width: 56, height: 56, mx: 'auto', borderRadius: '999px', border: '2px dashed', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.secondary' }}>
                                                    <AddIcon sx={{ fontSize: '1.625rem' }} />
                                                </Box>
                                                <Typography sx={{ mt: 1, fontSize: '0.75rem', fontWeight: 600, color: 'text.secondary' }}>Agregar</Typography>
                                            </Box>
                                        </Grid>
                                    )}
                                </Grid>
                            )
                        )}
                    </Box>

                    <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                            <Typography sx={{ fontSize: '1.125rem', fontWeight: 800, color: 'text.primary' }}>Actividad Reciente</Typography>
                        </Box>

                        <Stack spacing={1.5}>
                            {(status.recent_activity || []).length === 0 && (
                                <Card sx={{ borderRadius: '10px', boxShadow: 0, border: '1px solid', borderColor: 'divider' }}>
                                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                                        <Typography sx={{ fontSize: '0.8125rem', fontWeight: 700, color: 'text.primary' }}>
                                            Sin actividad reciente
                                        </Typography>
                                        <Typography sx={{ fontSize: '0.75rem', fontWeight: 500, color: 'text.secondary' }}>
                                            Aquí verás tus próximas reservaciones, visitas y reportes.
                                        </Typography>
                                    </CardContent>
                                </Card>
                            )}
                            {(status.recent_activity || []).map((item) => (
                                <Card
                                    key={item.id}
                                    sx={{ borderRadius: '10px', boxShadow: 0, border: '1px solid', borderColor: 'divider', cursor: item.path ? 'pointer' : 'default' }}
                                    onClick={item.path ? () => navigateToPath(item.path) : undefined}
                                >
                                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
                                                <Box sx={{ width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                                    {activityIcon(item.icon)}
                                                </Box>
                                                <Box sx={{ minWidth: 0 }}>
                                                    <Typography sx={{ fontSize: '0.8125rem', fontWeight: 800, color: 'text.primary', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                        {item.title}
                                                    </Typography>
                                                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.secondary', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                        {item.subtitle}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                            {item.type === 'visit_entry' ? (
                                                <SolarIcon name="check-circle" size={28} color="success.main" />
                                            ) : item.badge?.label ? (
                                                <Box sx={badgeSx(item.badge?.color)}>{item.badge?.label}</Box>
                                            ) : null}
                                        </Box>
                                    </CardContent>
                                </Card>
                            ))}
                        </Stack>
                    </Box>
                </Box>
            </Box>

            <Dialog open={addPickerOpen} onClose={() => setAddPickerOpen(false)} fullWidth maxWidth="xs">
                <DialogTitle>Agregar acción rápida</DialogTitle>
                <DialogContent>
                    {availableToAdd.length === 0 ? (
                        <Typography sx={{ py: 2, color: 'text.secondary' }}>
                            Todas las acciones disponibles ya están visibles.
                        </Typography>
                    ) : (
                        <List dense>
                            {availableToAdd.map((action) => (
                                <ListItem key={action.id} disablePadding>
                                    <ListItemButton onClick={() => handleAddQuickAction(action.id)}>
                                        <ListItemIcon sx={{ minWidth: 40 }}>{action.icon}</ListItemIcon>
                                        <ListItemText primary={action.label} />
                                    </ListItemButton>
                                </ListItem>
                            ))}
                        </List>
                    )}
                </DialogContent>
            </Dialog>
            <WeatherModal open={weatherModalOpen} onClose={() => setWeatherModalOpen(false)} />
            <LocationModal open={locationModalOpen} onClose={() => setLocationModalOpen(false)} />
        </Container>
    );
};

export default ResidentHub;